import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { callHandlerJson, IframeBridge } from '@alga-psa/extension-iframe-sdk';
import {
  Alert,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  LoadingIndicator,
  Separator,
  Stack,
  Tabs,
  Text,
} from '@alga-psa/ui-kit';
import type { ApplianceDetail, AuditLogEntry, BillingInfo, DetailAppliance, Envelope } from './types';
import {
  CopyButton,
  DateText,
  ExternalLink,
  KV,
  LicenseExpBadge,
  SectionCard,
  UnixDateText,
  codeStateTone,
  entitlementKindTone,
  fmtDate,
  fmtDateTime,
  fmtUnixDate,
  fmtUnixDateTime,
  relFromMs,
  relTime,
  statusTone,
} from './shared';
import {
  AirgapKeyDialog,
  CancelTenantDialog,
  ChangeEntitlementDialog,
  EditTenantDialog,
  ExtendProDialog,
  PauseBillingDialog,
  ReissueActivationCodeDialog,
  ReissueInstallCodeDialog,
  ResumeBillingDialog,
  RevokeApplianceDialog,
  RevokeDialog,
  SetStatusDialog,
} from './actions';

type DialogKey =
  | 'reissue-install' | 'reissue-activation' | 'airgap' | 'extend-pro' | 'entitlement'
  | 'pause-billing' | 'resume-billing' | 'suspend' | 'reactivate' | 'revoke' | 'cancel' | 'edit' | 'revoke-appliance';

/** The one license expiry that matters: the latest appliance token, else the entitlement's own. */
function effectiveLicenseExp(d: ApplianceDetail): number | null {
  const exps = d.appliances.filter((a) => !a.credential_revoked && a.token_exp != null).map((a) => a.token_exp as number);
  if (exps.length) return Math.max(...exps);
  return d.entitlement?.ends_at ?? null;
}

const stripeStatusTone = (s: string | undefined | null) =>
  s === 'active' ? 'success' : s === 'trialing' ? 'info' : s === 'past_due' || s === 'unpaid' ? 'warning' : s === 'canceled' ? 'danger' : 'default';

export function InstallDetail({ bridge, tenantId, onBack, onChanged }: {
  bridge: IframeBridge; tenantId: string; onBack: () => void; onChanged: () => void;
}) {
  const [detail, setDetail] = useState<ApplianceDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [audit, setAudit] = useState<AuditLogEntry[] | null>(null);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [tab, setTab] = useState('overview');
  const [dialog, setDialog] = useState<DialogKey | null>(null);
  const [applianceToRevoke, setApplianceToRevoke] = useState<DetailAppliance | null>(null);
  const [version, setVersion] = useState(0);

  const base = `/installs/${encodeURIComponent(tenantId)}`;

  useEffect(() => {
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        const res = await callHandlerJson<Envelope<ApplianceDetail>>(bridge, base, { method: 'GET' });
        if (cancelled) return;
        if (!res?.success) throw new Error(res?.error || 'Failed to load appliance');
        // Tolerate the Phase 1 payload shape during the rollout window.
        const d = res.data;
        if (!d.codes && d.install_codes) d.codes = d.install_codes;
        d.codes = (d.codes ?? []).map((c) => ({ ...c, state: c.state ?? (c.revoked ? 'revoked' : c.consumed ? 'consumed' : c.expires_at * 1000 < Date.now() ? 'expired' : 'live'), purpose: c.purpose ?? 'install' }));
        d.appliances = (d.appliances ?? []).map((a) => ({ ...a, credential_revoked: a.credential_revoked ?? !!a.revoked }));
        setDetail(d);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [bridge, base, version]);

  // Billing is only meaningful for Stripe-backed entitlements; the route returns { billing: null } otherwise.
  useEffect(() => {
    if (!detail) return;
    if (!detail.entitlement || detail.entitlement.kind !== 'stripe') { setBilling(null); setBillingError(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await callHandlerJson<Envelope<BillingInfo>>(bridge, `${base}/billing`, { method: 'GET' });
        if (cancelled) return;
        if (!res?.success) throw new Error(res?.error || 'Failed to load billing');
        setBilling(res.data);
        setBillingError(null);
      } catch (e) {
        if (!cancelled) { setBilling(null); setBillingError(e instanceof Error ? e.message : String(e)); }
      }
    })();
    return () => { cancelled = true; };
  }, [bridge, base, detail]);

  const loadAudit = useCallback(async () => {
    try {
      const res = await callHandlerJson<Envelope<AuditLogEntry[]>>(bridge, `${base}/audit?limit=100`, { method: 'GET' });
      if (!res?.success) throw new Error(res?.error || 'Failed to load audit log');
      setAudit(res.data ?? []);
      setAuditError(null);
    } catch (e) {
      setAuditError(e instanceof Error ? e.message : String(e));
    }
  }, [bridge, base]);

  useEffect(() => { if (tab === 'audit') void loadAudit(); }, [tab, loadAudit, version]);

  // After any action completes: reload the detail, the audit, and tell the list to refresh.
  const refresh = useCallback(() => { setVersion((v) => v + 1); onChanged(); }, [onChanged]);

  const ent = detail?.entitlement ?? null;
  const status = detail?.tenant.status;
  const activeStripe = !!ent && ent.active && ent.kind === 'stripe';
  const activeEnt = !!ent && ent.active;
  const cancelled = status === 'cancelled';
  const suspended = status === 'suspended';
  const paused = !!billing?.billing?.pause_collection;
  const licenseExp = detail ? effectiveLicenseExp(detail) : null;

  const tabs = useMemo(() => {
    if (!detail) return [];
    return [
      { key: 'overview', label: 'Overview', content: <OverviewTab detail={detail} billing={billing} licenseExp={licenseExp} /> },
      { key: 'licensing', label: 'Licensing', content: (
        <LicensingTab detail={detail} billing={billing} billingError={billingError} onRevokeAppliance={(a) => { setApplianceToRevoke(a); setDialog('revoke-appliance'); }} />
      ) },
      { key: 'codes', label: `Codes (${detail.codes.length})`, content: <CodesTab detail={detail} /> },
      { key: 'audit', label: 'Audit', content: <AuditTab rows={audit} error={auditError} onReload={loadAudit} /> },
    ];
  }, [detail, billing, billingError, licenseExp, audit, auditError, loadAudit]);

  const close = () => { setDialog(null); setApplianceToRevoke(null); };

  return (
    <Stack gap={16}>
      <Breadcrumbs
        items={[
          { label: 'Appliance Installs', onClick: onBack },
          { label: detail?.tenant.company_name ?? 'Loading…' },
        ]}
      />

      {error && <Alert tone="danger"><Text size="sm">{error}</Text></Alert>}
      {!detail && !error && <Card><LoadingIndicator text="Loading appliance…" /></Card>}

      {detail && (
        <>
          <Stack direction="row" gap={12} align="center" justify="space-between" style={{ flexWrap: 'wrap' }}>
            <Stack gap={4}>
              <Stack direction="row" gap={8} align="center" style={{ flexWrap: 'wrap' }}>
                <Text as="strong" size="lg" weight={600}>{detail.tenant.company_name}</Text>
                <Badge tone={statusTone[detail.tenant.status]}>{detail.tenant.status}</Badge>
                {ent && <Badge tone={entitlementKindTone[ent.kind]}>{ent.kind === 'comp' ? `comp until ${fmtUnixDate(ent.ends_at)}` : `${ent.tier} · stripe`}</Badge>}
              </Stack>
              <Stack direction="row" gap={6} align="center" style={{ flexWrap: 'wrap' }}>
                <Text size="sm" tone="muted">{detail.tenant.contact_email} ·</Text>
                <code>{detail.tenant.tenant_id}</code>
                <CopyButton value={detail.tenant.tenant_id} />
              </Stack>
            </Stack>
            <Stack direction="row" gap={8} align="center">
              <Button variant="outline" size="sm" onClick={() => setVersion((v) => v + 1)}>Refresh</Button>
              <Button variant="outline" onClick={onBack}>← Back</Button>
            </Stack>
          </Stack>

          {/* Action bar: buttons are enabled by state (plan §6.4). */}
          <Card style={{ padding: '10px 16px' }}>
            <Stack direction="row" gap={8} align="center" style={{ flexWrap: 'wrap' }}>
              <Text size="xs" tone="muted" weight={600} style={{ textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 4 }}>Actions</Text>
              <Button size="sm" variant="outline" disabled={cancelled || suspended} onClick={() => setDialog('reissue-install')}>Reissue install code</Button>
              <Button size="sm" variant="outline" disabled={!activeEnt || cancelled || suspended} onClick={() => setDialog('reissue-activation')}>Reissue activation code</Button>
              <Button size="sm" variant="outline" disabled={!activeEnt || cancelled || suspended} onClick={() => setDialog('airgap')}>Re-sign air-gap key</Button>
              <Button size="sm" variant="outline" disabled={activeStripe || cancelled || suspended} onClick={() => setDialog('extend-pro')}>Extend Pro</Button>
              <Button size="sm" variant="outline" disabled={!activeStripe || cancelled || suspended} onClick={() => setDialog('entitlement')}>Change seats</Button>
              {paused
                ? <Button size="sm" variant="outline" disabled={!activeStripe} onClick={() => setDialog('resume-billing')}>Resume billing</Button>
                : <Button size="sm" variant="outline" disabled={!activeStripe || cancelled} onClick={() => setDialog('pause-billing')}>Pause billing</Button>}
              {suspended
                ? <Button size="sm" variant="outline" onClick={() => setDialog('reactivate')}>Reactivate</Button>
                : <Button size="sm" variant="outline" disabled={cancelled} onClick={() => setDialog('suspend')}>Suspend</Button>}
              <Button size="sm" variant="outline" onClick={() => setDialog('edit')}>Edit tenant</Button>
              <span style={{ flex: 1 }} />
              <Button size="sm" variant="destructive" disabled={!activeEnt} onClick={() => setDialog('revoke')}>Revoke</Button>
              <Button size="sm" variant="destructive" disabled={cancelled} onClick={() => setDialog('cancel')}>Cancel tenant</Button>
            </Stack>
          </Card>

          <Tabs tabs={tabs} activeKey={tab} onChange={setTab} />

          {/* Dialogs are mounted only while open so each opens with fresh form state. */}
          {dialog === 'reissue-install' && <ReissueInstallCodeDialog bridge={bridge} detail={detail} isOpen onClose={close} onDone={refresh} />}
          {dialog === 'reissue-activation' && <ReissueActivationCodeDialog bridge={bridge} detail={detail} isOpen onClose={close} onDone={refresh} />}
          {dialog === 'airgap' && <AirgapKeyDialog bridge={bridge} detail={detail} isOpen onClose={close} onDone={refresh} />}
          {dialog === 'extend-pro' && <ExtendProDialog bridge={bridge} detail={detail} isOpen onClose={close} onDone={refresh} />}
          {dialog === 'entitlement' && <ChangeEntitlementDialog bridge={bridge} detail={detail} billing={billing} isOpen onClose={close} onDone={refresh} />}
          {dialog === 'pause-billing' && <PauseBillingDialog bridge={bridge} detail={detail} isOpen onClose={close} onDone={refresh} />}
          {dialog === 'resume-billing' && <ResumeBillingDialog bridge={bridge} detail={detail} isOpen onClose={close} onDone={refresh} />}
          {dialog === 'suspend' && <SetStatusDialog bridge={bridge} detail={detail} target="suspended" isOpen onClose={close} onDone={refresh} />}
          {dialog === 'reactivate' && <SetStatusDialog bridge={bridge} detail={detail} target="active" isOpen onClose={close} onDone={refresh} />}
          {dialog === 'revoke' && <RevokeDialog bridge={bridge} detail={detail} isOpen onClose={close} onDone={refresh} />}
          {dialog === 'cancel' && <CancelTenantDialog bridge={bridge} detail={detail} isOpen onClose={close} onDone={refresh} />}
          {dialog === 'edit' && <EditTenantDialog bridge={bridge} detail={detail} isOpen onClose={close} onDone={refresh} />}
          {dialog === 'revoke-appliance' && <RevokeApplianceDialog bridge={bridge} detail={detail} appliance={applianceToRevoke} isOpen onClose={close} onDone={refresh} />}
        </>
      )}
    </Stack>
  );
}

// ── Tabs ─────────────────────────────────────────────────────────────────────

function StatusTile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack gap={4} style={{ minWidth: 140 }}>
      <Text size="xs" tone="muted" weight={600} style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Text>
      <div>{children}</div>
    </Stack>
  );
}

function OverviewTab({ detail, billing, licenseExp }: { detail: ApplianceDetail; billing: BillingInfo | null; licenseExp: number | null }) {
  const ent = detail.entitlement;
  const lastCheckin = detail.appliances.reduce<string | null>((acc, a) => (a.last_checkin_at && (!acc || a.last_checkin_at > acc) ? a.last_checkin_at : acc), null);
  return (
    <Stack gap={16} style={{ paddingTop: 12 }}>
      <Card style={{ padding: '12px 16px' }}>
        <Stack direction="row" gap={24} style={{ flexWrap: 'wrap' }}>
          <StatusTile label="Registry"><Badge tone={statusTone[detail.tenant.status]}>{detail.tenant.status}</Badge></StatusTile>
          <StatusTile label="Entitlement">
            {ent ? <Badge tone={ent.active ? 'success' : 'danger'}>{ent.active ? 'active' : 'revoked'} · {ent.kind}</Badge> : <Badge tone="default">none</Badge>}
          </StatusTile>
          <StatusTile label="License"><LicenseExpBadge exp={licenseExp} /></StatusTile>
          <StatusTile label="Last check-in">
            {lastCheckin ? <Text size="sm">{relTime(lastCheckin)}</Text> : <Text size="sm" tone="muted">{ent ? 'never' : 'n/a'}</Text>}
          </StatusTile>
          <StatusTile label="Stripe">
            {ent?.kind === 'stripe'
              ? billing?.billing ? <Badge tone={stripeStatusTone(billing.billing.status)}>{billing.billing.status}</Badge> : <Text size="sm" tone="muted">loading…</Text>
              : <Text size="sm" tone="muted">n/a</Text>}
          </StatusTile>
        </Stack>
      </Card>

      <SectionCard title="Tenant">
        <KV k="Edition" v={detail.tenant.edition} />
        <KV k="Product" v={detail.tenant.product_code} />
        <KV k="Status" v={<Badge tone={statusTone[detail.tenant.status]}>{detail.tenant.status}</Badge>} />
        <KV k="Region" v={detail.tenant.region ?? '—'} />
        <KV k="Registered" v={<DateText iso={detail.tenant.registered_at} />} />
        <KV k="Installed" v={<DateText iso={detail.tenant.installed_at} />} />
        <KV k="Contact" v={detail.tenant.contact_name ?? '—'} />
        <KV k="Contact email" v={detail.tenant.contact_email} />
        {detail.tenant.stripe_customer_id && (
          <KV k="Stripe customer" v={(
            <Stack direction="row" gap={6} align="center" style={{ flexWrap: 'wrap' }}>
              <code>{detail.tenant.stripe_customer_id}</code>
              <CopyButton value={detail.tenant.stripe_customer_id} />
            </Stack>
          )} />
        )}
      </SectionCard>
    </Stack>
  );
}

function LicensingTab({ detail, billing, billingError, onRevokeAppliance }: {
  detail: ApplianceDetail; billing: BillingInfo | null; billingError: string | null; onRevokeAppliance: (a: DetailAppliance) => void;
}) {
  const ent = detail.entitlement;
  return (
    <Stack gap={16} style={{ paddingTop: 12 }}>
      <SectionCard title="Entitlement">
        {ent ? (
          <>
            <KV k="Kind" v={<Badge tone={entitlementKindTone[ent.kind]}>{ent.kind}</Badge>} />
            <KV k="Tier" v={ent.tier} />
            <KV k="Seats" v={String(ent.seats ?? 'unlimited')} />
            <KV k="Active" v={<Badge tone={ent.active ? 'success' : 'danger'}>{ent.active ? 'yes' : 'no'}</Badge>} />
            <KV k="License issued" v={<Badge tone={ent.has_license ? 'success' : 'warning'}>{ent.has_license ? 'yes' : 'not yet'}</Badge>} />
            <KV k="Transport" v={ent.transport ?? '—'} />
            {ent.kind === 'comp' && <KV k="Comp ends" v={<UnixDateText sec={ent.ends_at} withTime />} />}
            {ent.kind === 'comp' && <KV k="Note" v={ent.note ?? '—'} />}
            <KV k="License id" v={ent.license_sub ? <code>{ent.license_sub}</code> : '—'} />
            <KV k="Customer" v={ent.customer} />
            <KV k={ent.kind === 'comp' ? 'Comp id' : 'Stripe sub'} v={(
              <Stack direction="row" gap={6} align="center" style={{ flexWrap: 'wrap' }}>
                <code>{ent.stripe_sub_id}</code>
                <CopyButton value={ent.stripe_sub_id} />
              </Stack>
            )} />
            <KV k="Created" v={<DateText iso={ent.created_at ?? null} withTime />} />
            <KV k="Updated" v={<DateText iso={ent.updated_at ?? null} withTime />} />
          </>
        ) : (
          <Text size="sm" tone="muted">Essentials — no paid entitlement. Use “Extend Pro” to grant time-boxed Pro without billing.</Text>
        )}
      </SectionCard>

      <SectionCard title="Appliances" count={detail.appliances.length}>
        {detail.appliances.length === 0 ? (
          <Text size="sm" tone="muted">None. {ent ? 'No appliance has redeemed an activation code yet.' : ''}</Text>
        ) : (
          <Stack gap={8}>
            {detail.appliances.map((a, i) => {
              const boundOk = a.token_aud == null || a.token_aud === detail.tenant.tenant_id;
              return (
                <React.Fragment key={a.appliance_id}>
                  {i > 0 && <Separator />}
                  <Stack gap={6}>
                    <Stack direction="row" gap={12} align="center" style={{ flexWrap: 'wrap' }}>
                      <code style={{ flex: 1, minWidth: 160 }}>{a.appliance_id}</code>
                      <Badge tone={a.credential_revoked ? 'danger' : 'success'}>{a.credential_revoked ? 'credential revoked' : 'credential active'}</Badge>
                      <LicenseExpBadge exp={a.credential_revoked ? null : a.token_exp} />
                      {!a.credential_revoked && <Button size="xs" variant="ghost" onClick={() => onRevokeAppliance(a)}>Revoke credential</Button>}
                    </Stack>
                    <Stack direction="row" gap={16} style={{ flexWrap: 'wrap' }}>
                      <Text size="xs" tone="muted">token expires {a.token_exp != null ? `${fmtUnixDateTime(a.token_exp)} · ${relFromMs(a.token_exp * 1000)}` : 'none'}</Text>
                      <Text size="xs" tone="muted">issued {a.token_iat != null ? `${fmtUnixDateTime(a.token_iat)} · ${relFromMs(a.token_iat * 1000)}` : '—'}</Text>
                      <Text size="xs" tone="muted">last check-in {a.last_checkin_at ? `${fmtDateTime(a.last_checkin_at)} · ${relTime(a.last_checkin_at)}` : 'never'}</Text>
                      <Text size="xs" tone={boundOk ? 'muted' : undefined} style={boundOk ? undefined : { color: 'var(--alga-danger)' }}>
                        {a.token_aud == null ? 'unbound token' : boundOk ? 'bound to this tenant' : `bound to ${a.token_aud} (mismatch)`}
                      </Text>
                    </Stack>
                  </Stack>
                </React.Fragment>
              );
            })}
          </Stack>
        )}
      </SectionCard>

      <SectionCard title="Billing (Stripe)">
        {!ent || ent.kind !== 'stripe' ? (
          <Text size="sm" tone="muted">{ent?.kind === 'comp' ? 'Comp entitlement — nothing is billed.' : 'No subscription.'}</Text>
        ) : billingError ? (
          <Alert tone="warning"><Text size="sm">Could not load the Stripe subscription: {billingError}</Text></Alert>
        ) : !billing ? (
          <LoadingIndicator text="Loading subscription…" />
        ) : !billing.billing ? (
          <Alert tone="warning"><Text size="sm">Stripe has no subscription <code>{ent.stripe_sub_id}</code>. The entitlement is orphaned.</Text></Alert>
        ) : (
          <>
            {billing.drift.status_mismatch && (
              <Alert tone="warning"><Text size="sm">Stripe status is <b>{billing.billing.status}</b> while the entitlement is {ent.active ? 'active' : 'revoked'}. The license and billing disagree.</Text></Alert>
            )}
            {billing.drift.seats_mismatch && (
              <Alert tone="warning"><Text size="sm">Stripe bills {billing.billing.seat_quantity ?? '?'} seat(s) while the license allows {billing.entitlement_seats ?? 'unlimited'}.</Text></Alert>
            )}
            <div style={{ height: 8 }} />
            <KV k="Status" v={<Badge tone={stripeStatusTone(billing.billing.status)}>{billing.billing.status}</Badge>} />
            <KV k="Seats billed" v={String(billing.billing.seat_quantity ?? '—')} />
            <KV k="Interval" v={billing.billing.interval ?? '—'} />
            <KV k="Current period ends" v={<UnixDateText sec={billing.billing.current_period_end} />} />
            <KV k="Cancels" v={billing.billing.cancel_at ? <UnixDateText sec={billing.billing.cancel_at} /> : billing.billing.cancel_at_period_end ? 'at period end' : '—'} />
            <KV k="Collection" v={billing.billing.pause_collection
              ? <Badge tone="warning">paused ({billing.billing.pause_collection.behavior}){billing.billing.pause_collection.resumes_at ? ` until ${fmtUnixDate(billing.billing.pause_collection.resumes_at)}` : ''}</Badge>
              : <Badge tone="success">collecting</Badge>} />
            <KV k="Links" v={(
              <Stack direction="row" gap={12} style={{ flexWrap: 'wrap' }}>
                <ExternalLink href={billing.billing.dashboard_subscription_url}>Subscription in Stripe</ExternalLink>
                {billing.billing.dashboard_customer_url && <ExternalLink href={billing.billing.dashboard_customer_url}>Customer in Stripe</ExternalLink>}
                {!billing.billing.livemode && <Badge tone="warning">test mode</Badge>}
              </Stack>
            )} />
          </>
        )}
      </SectionCard>
    </Stack>
  );
}

function CodesTab({ detail }: { detail: ApplianceDetail }) {
  return (
    <Stack gap={16} style={{ paddingTop: 12 }}>
      <SectionCard title="Code history" count={detail.codes.length}>
        {detail.codes.length === 0 ? (
          <Text size="sm" tone="muted">None.</Text>
        ) : (
          <Stack gap={8}>
            {detail.codes.map((c, i) => (
              <React.Fragment key={`${c.code_masked}-${c.created_at}-${i}`}>
                {i > 0 && <Separator />}
                <Stack direction="row" gap={12} align="center" style={{ flexWrap: 'wrap' }}>
                  <code style={{ flex: 1, minWidth: 100 }}>…{c.code_masked}</code>
                  <Badge tone="default">{c.purpose === 'activation' ? 'activation code' : 'install code'}</Badge>
                  <Badge tone={codeStateTone[c.state]}>{c.state}</Badge>
                  <Text size="xs" tone="muted">generated {fmtDate(c.created_at)}</Text>
                  <Text size="xs" tone="muted">expires {fmtUnixDate(c.expires_at)}</Text>
                </Stack>
              </React.Fragment>
            ))}
          </Stack>
        )}
      </SectionCard>
      <Text size="xs" tone="muted">
        Install codes are redeemed by the setup wizard (30-day TTL). Activation codes are entered in Settings → License for an upgrade or rebind (1-day TTL; minting one revokes prior appliance credentials).
      </Text>
    </Stack>
  );
}

const auditTone = (s: string | null) => (s === 'completed' ? 'success' : s === 'failed' ? 'danger' : s === 'pending' || s === 'running' ? 'info' : 'default');

function AuditTab({ rows, error, onReload }: { rows: AuditLogEntry[] | null; error: string | null; onReload: () => void }) {
  return (
    <Stack gap={16} style={{ paddingTop: 12 }}>
      <SectionCard title="Audit" count={rows?.length} action={<Button size="xs" variant="ghost" onClick={onReload}>Reload</Button>}>
        {error && <Alert tone="danger"><Text size="sm">{error}</Text></Alert>}
        {!rows && !error && <LoadingIndicator text="Loading audit log…" />}
        {rows && rows.length === 0 && <Text size="sm" tone="muted">No operator actions recorded for this tenant.</Text>}
        {rows && rows.length > 0 && (
          <Stack gap={8}>
            {rows.map((r, i) => {
              const d = r.details ?? {};
              const reason = typeof d.reason === 'string' ? d.reason : null;
              const mode = typeof d.mode === 'string' ? d.mode : null;
              return (
                <React.Fragment key={r.log_id}>
                  {i > 0 && <Separator />}
                  <Stack gap={4}>
                    <Stack direction="row" gap={12} align="center" style={{ flexWrap: 'wrap' }}>
                      <Text size="sm" weight={600} style={{ flex: 1, minWidth: 160 }}>{r.event_type}</Text>
                      {r.status && <Badge tone={auditTone(r.status)}>{r.status}</Badge>}
                      {mode && <Badge tone="default">{mode}</Badge>}
                      <Text size="xs" tone="muted">{r.user_email ?? 'system'}</Text>
                      <Text size="xs" tone="muted">{fmtDateTime(r.created_at)} · {relTime(r.created_at)}</Text>
                    </Stack>
                    {reason && <Text size="xs" tone="muted">Reason: {reason}</Text>}
                    {r.error_message && <Text size="xs" style={{ color: 'var(--alga-danger)' }}>{r.error_message}</Text>}
                    {r.workflow_id && <Text size="xs" tone="muted"><code>{r.workflow_id}</code></Text>}
                  </Stack>
                </React.Fragment>
              );
            })}
          </Stack>
        )}
      </SectionCard>
    </Stack>
  );
}
